import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ModelSelectorButton.tsx -- top-bar "Models" button + capability-
// selector dropdown.
//
// User direction (verbatim, this session):
//   "we need in the dashboard in the top bar section to select
//    whatever models we want for different capabilities, so there
//    should be a dropdown for each primary capability or if it's too
//    many to fit in the bar show the main chat model for the tablet
//    view but a button to do a dropdown menu showing the different
//    capabilities and being able to have dropdown options to select
//    the different models"
//
// MVP:
//   - Single button "Models · <chat-model-name>" matching the user's
//     "tablet view" idea. Always shown.
//   - Click -> dropdown listing the 5 capability slots (chat, image-
//     generation, embedding, code-interpreter, vision). Each slot shows
//     the current model + a sub-dropdown of compatible models.
//   - Pick a model -> PUT /llm/config -> update local state.
//   - Revert button per slot to clear the tenant override (falls back
//     to platform default).
//   - Role-gated: read-only display when role != full; only 'full'
//     can change.
//
// Out of scope for MVP (deferred):
//   - Per-coworker overrides (D.8 wave)
//   - Per-project model overrides (would need new formation)
//   - Audio capability slots (niche)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../utils/api.js';
const CAPABILITY_LABELS = {
    chat: 'Chat',
    'image-generation': 'Image Generation',
    embedding: 'Embedding',
    'code-interpreter': 'Code Interpreter',
    vision: 'Vision',
};
const CAPABILITY_DESCRIPTIONS = {
    chat: 'Default model for conversations and tool-using agents',
    'image-generation': 'Generate images (.png, .jpg, .webp) from text prompts',
    embedding: 'Vector embeddings for semantic search',
    'code-interpreter': 'Generate spreadsheets, slides, PDFs via Python execution',
    vision: 'Read and describe images',
};
export function ModelSelectorButton({ projectId, role }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [models, setModels] = useState([]);
    const [capabilitySlots, setCapabilitySlots] = useState([]);
    const [config, setConfig] = useState([]);
    const wrapRef = useRef(null);
    const canEdit = role === 'full';
    // Lazy-load on first open. Keeps the initial dashboard render light.
    const refresh = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const [m, c] = await Promise.all([
                api.get(`/p/${projectId}/llm/models`),
                api.get(`/p/${projectId}/llm/config`),
            ]);
            setModels(m.models);
            setCapabilitySlots(m.capabilitySlots);
            setConfig(c.capabilities);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load model catalog');
        }
        finally {
            setLoading(false);
        }
    }, [projectId]);
    // First open triggers a load. Subsequent opens use cached data.
    useEffect(() => {
        if (open && models.length === 0 && !loading && !err) {
            void refresh();
        }
    }, [open, models.length, loading, err, refresh]);
    // Outside click + Escape close.
    useEffect(() => {
        if (!open)
            return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    // Persist a capability override.
    const setCapability = useCallback(async (capability, defaultModel) => {
        if (!canEdit)
            return;
        try {
            await api.put(`/p/${projectId}/llm/config`, { capability, defaultModel });
            // Optimistic refresh: re-fetch config to get the resolved
            // source (tenant vs platform-default vs unset). Cheap.
            const c = await api.get(`/p/${projectId}/llm/config`);
            setConfig(c.capabilities);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to update');
        }
    }, [projectId, canEdit]);
    // Find the chat model display name for the button label. Falls back
    // gracefully when chat is unset (button reads "Models").
    const chatModelLabel = useMemo(() => {
        const chatState = config.find((c) => c.capability === 'chat');
        if (!chatState?.defaultModel)
            return null;
        const m = models.find((mm) => mm.modelId === chatState.defaultModel);
        return m?.displayName ?? chatState.defaultModel;
    }, [config, models]);
    // Group models by capability for the per-slot dropdowns.
    const modelsByCapability = useMemo(() => {
        const map = new Map();
        for (const cap of capabilitySlots) {
            map.set(cap, models.filter((m) => m.capabilities.includes(cap)));
        }
        return map;
    }, [models, capabilitySlots]);
    return (_jsxs("div", { className: "model-selector-wrap", ref: wrapRef, children: [_jsxs("button", { type: "button", className: "model-selector-button", onClick: () => setOpen((v) => !v), title: "Configure models per capability", "aria-expanded": open, children: [_jsx("span", { className: "model-selector-button-glyph", "aria-hidden": true, children: '\u2699' }), _jsx("span", { className: "model-selector-button-label", children: chatModelLabel ? `Models · ${chatModelLabel}` : 'Models' })] }), open && (_jsxs("div", { className: "model-selector-dropdown", role: "dialog", "aria-label": "Model selectors", children: [_jsxs("div", { className: "model-selector-header", children: [_jsx("span", { className: "model-selector-title", children: "Models per capability" }), _jsx("button", { type: "button", className: "model-selector-close", onClick: () => setOpen(false), "aria-label": "Close", children: '\u2715' })] }), _jsxs("div", { className: "model-selector-body", children: [loading && _jsx("div", { className: "model-selector-loading", children: 'Loading models\u2026' }), err && _jsx("div", { className: "model-selector-error", children: err }), !loading && !err && capabilitySlots.map((cap) => {
                                const state = config.find((c) => c.capability === cap);
                                const choices = modelsByCapability.get(cap) ?? [];
                                // Resolved default == what raindb's router will actually
                                // use right now if a caller sends no model. May come from
                                // the tenant override OR the platform fallback chain.
                                const resolvedDefault = state?.defaultModel ?? null;
                                const resolvedModel = resolvedDefault
                                    ? models.find((m) => m.modelId === resolvedDefault)
                                    : null;
                                // Tenant override wins; otherwise the dropdown shows
                                // "" which represents "use whatever the platform
                                // resolves" -- we surface the resolved model name in
                                // the placeholder so the user sees it.
                                const isOverridden = state?.source === 'tenant';
                                const selectValue = isOverridden ? (resolvedDefault ?? '') : '';
                                // The placeholder option text. When the platform
                                // resolves to a real model, label it that way; if the
                                // platform has nothing either, say so.
                                const placeholderLabel = isOverridden
                                    ? '(use platform default)'
                                    : resolvedModel
                                        ? `default: ${resolvedModel.displayName}`
                                        : resolvedDefault
                                            ? `default: ${resolvedDefault}`
                                            : '(no default)';
                                // Source chip text + class.
                                const sourceLabel = state?.source === 'tenant' ? 'tenant override'
                                    : state?.source === 'platform-public' ? 'platform default'
                                        : state?.source === 'platform-default' ? 'platform locked'
                                            : state?.source === 'model-registry-default' ? 'registered default'
                                                : state?.source === 'model-registry-fallback' ? 'first available model'
                                                    : null;
                                // For caps with no models registered with this exact
                                // capability, the dropdown is disabled but we still
                                // want to show what model would actually be used as
                                // fallback. Empty choices + a known resolvedModel
                                // means: "no images model registered yet, but here's
                                // what would run". Add the fallback model to choices
                                // so it can be rendered as the only option.
                                const augmentedChoices = choices.length > 0
                                    ? choices
                                    : (resolvedModel ? [resolvedModel] : []);
                                return (_jsxs("div", { className: "model-selector-row", children: [_jsxs("div", { className: "model-selector-row-label", children: [_jsx("span", { className: "model-selector-row-name", children: CAPABILITY_LABELS[cap] ?? cap }), _jsx("span", { className: "model-selector-row-desc", children: CAPABILITY_DESCRIPTIONS[cap] ?? '' })] }), _jsxs("div", { className: "model-selector-row-control", children: [augmentedChoices.length === 0 ? (_jsx("span", { className: "model-selector-empty", children: "no models with this capability" })) : (_jsxs("select", { className: "model-selector-select", value: selectValue, disabled: !canEdit, onChange: (e) => {
                                                        const v = e.target.value;
                                                        void setCapability(cap, v || null);
                                                    }, title: canEdit ? '' : 'Read-only: requires full role', children: [_jsx("option", { value: "", children: placeholderLabel }), augmentedChoices.map((m) => (_jsx("option", { value: m.modelId, children: m.displayName }, m.modelId)))] })), sourceLabel && (_jsx("span", { className: `model-selector-source model-selector-source-${state?.source ?? 'unset'}`, children: sourceLabel }))] })] }, cap));
                            })] }), !canEdit && (_jsx("div", { className: "model-selector-footer", children: "Read-only. Models can be configured by users with full project role." }))] }))] }));
}
